// ============================================================
// TILT — MENUS
// ------------------------------------------------------------
// Table select · Loadout builder · Gauntlet ladder.
//
// UI palette ONLY. All chrome. (theme.js LAW 1.)
//
// ── ON THE LOADOUT SCREEN ────────────────────────────────────
// This screen is not decoration. It is where TILT's pre-flip
// agency lives: you choose the four verbs you'll have available
// BEFORE you see the table. Choosing wrong is a real loss, and
// choosing well feels like cheating. That's the whole point.
//
// Trial tables LOCK the loadout (the puzzle IS the loadout) —
// this screen respects that and shows the fixed kit read-only.
// Letting a player bring the wrong kit into a puzzle and then
// fail isn't difficulty, it's a bug.
// ============================================================

import { useState } from "react";
import { UI, PF, PAGE_BG, PANEL, PANEL_LIT, BRASS_METAL,
         facet, facetTB, RAISED, bloom, glow, SET, ensureKeyframes } from "../theme";
import { POWER_LIST, getPower, LOADOUT_SIZE, validateLoadout } from "../Powers";
import { tintOf } from "../theme";
import { TABLE_LIST } from "../tables";
import { GAUNTLET } from "../TiltNPC";
import { btnStyle } from "../HUD";
// THE MARK chip (with friends inside) is no longer drawn here: MarkCorner.js owns it, on every calm screen.

const KIND_META = {
  score: { label: "SCORE",  tint: UI.cool, blurb: "Play for the number." },
  trial: { label: "TRIAL",  tint: UI.good, blurb: "Solve it." },
  duel:  { label: "DUEL",   tint: UI.hot,  blurb: "Ruin someone." },
};

// ── SET HELPERS ───────────────────────────────────────────────
// `cleared` / `gauntlet` are MAPS now, not arrays — progression.js
// schema v1. (Arrays in RTDB are a concurrency trap: two clients
// appending to index 3 means one of them loses. Maps merge.)
//
// These two helpers accept EITHER shape, so the menus never have
// to care and an old save can't crash the ladder. One deriving
// function beats parallel copies that drift.
const has = (set, id) =>
  Array.isArray(set) ? set.includes(id) : !!(set && set[id]);
const count = (set) =>
  Array.isArray(set) ? set.length : Object.keys(set || {}).length;

const commas = (n) => (n || 0).toLocaleString("en-US");

// ── TITLE ─────────────────────────────────────────────────────
export function TitleScreen({ onPlay, onTrials, onDuel, onLoadout, onCoop, tokens = 0 }) {
  ensureKeyframes();
  return (
    <div style={{
      minHeight: "100vh", background: PAGE_BG, fontFamily: UI.font,
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center", gap: 8, position: "relative",
    }}>
      <div style={{
        fontFamily: UI.fontD, fontSize: 74, fontWeight: 900, letterSpacing: 18,
        color: UI.brassBright, textShadow: glow(UI.brassBright),
        animation: "tilt-flicker 4s linear infinite",
        marginRight: -18,
      }}>
        TILT
      </div>
      <div style={{
        fontSize: 12, letterSpacing: 6, color: UI.textFaint,
        marginBottom: 34, textTransform: "uppercase",
      }}>
        an illegal empire machine
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, width: 260 }}>
        <MenuBtn label="PLAY"     sub="Score tables"          tint={UI.cool}  onClick={onPlay} />
        <MenuBtn label="TRIALS"   sub="Puzzle tables"         tint={UI.good}  onClick={onTrials} />
        <MenuBtn label="GAUNTLET" sub="Duel the seven"        tint={UI.hot}   onClick={onDuel} />
        {onCoop && <MenuBtn label="CO-OP" sub="Take turns on Willie" tint={UI.chromeBright} onClick={onCoop} />}
        <MenuBtn label="LOADOUT"  sub="Choose your four"      tint={UI.brass} onClick={onLoadout} />
      </div>

      {/* ⧗ TILT TOKENS — TILT's OWN currency lane, and the only
          currency this app is EVER allowed to write. Terror writes
          divineCrests. Snake Eyes writes snakeEyesCoins. We write
          tiltTokens and nothing else. Exchanges happen at Pierre's
          Crest Well and NOWHERE ELSE. (Roadmap P1.3 — strict.) */}
      <div style={{
        marginTop: 26, display: "flex", alignItems: "center", gap: 7,
        padding: "6px 14px", clipPath: facetTB(5),
        background: PANEL, boxShadow: RAISED,
      }}>
        <span style={{
          fontSize: 13, color: UI.brassBright, textShadow: glow(UI.brassBright),
        }}>⧗</span>
        <span style={{
          fontFamily: UI.fontNum, fontSize: 13, letterSpacing: 1,
          color: tokens ? UI.brassBright : UI.textFaint,
        }}>{commas(tokens)}</span>
        <span style={{ fontSize: 8, letterSpacing: 2, color: UI.textFaint }}>
          TOKENS
        </span>
      </div>

      <div style={{
        marginTop: 28, fontSize: 10, color: UI.textFaint,
        letterSpacing: 2, fontStyle: "italic",
      }}>
        the ball obeys physics. you don't have to.
      </div>
    </div>
  );
}

function MenuBtn({ label, sub, tint, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        padding: "14px 20px", clipPath: facetTB(9),
        background: hov ? PANEL_LIT : PANEL, border: "none",
        boxShadow: hov ? bloom(tint) : RAISED,
        cursor: "pointer", textAlign: "left",
        transition: "box-shadow 140ms, background 140ms",
      }}
    >
      <div style={{
        fontFamily: UI.fontD, fontSize: 15, fontWeight: 700, letterSpacing: 4,
        color: tint, textShadow: glow(tint),
      }}>{label}</div>
      <div style={{ fontSize: 10, color: UI.textFaint, letterSpacing: 1, marginTop: 2 }}>
        {sub}
      </div>
    </button>
  );
}

// ── TABLE SELECT ──────────────────────────────────────────────
export function TableSelect({ kind, onPick, onBack, title, best = {}, cleared = {} }) {
  ensureKeyframes();
  const list = kind ? TABLE_LIST.filter((t) => t.kind === kind) : TABLE_LIST;

  return (
    <Shell title={title || "SELECT TABLE"} onBack={onBack}>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 460 }}>
        {list.map((t) => {
          const m    = KIND_META[t.kind];
          const hi   = best[t.id] || 0;
          const done = has(cleared, t.id);
          return (
            <button
              key={t.id}
              onClick={() => onPick(t)}
              style={{
                display: "flex", gap: 14, alignItems: "center",
                padding: "14px 16px", clipPath: facetTB(9),
                background: done ? PANEL_LIT : PANEL, border: "none",
                boxShadow: done ? bloom(UI.good) : RAISED,
                cursor: "pointer", textAlign: "left", width: "100%",
              }}
            >
              <TableThumb table={t} />
              <div style={{ flex: 1 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 8, marginBottom: 3,
                }}>
                  <span style={{
                    fontFamily: UI.fontD, fontSize: 15, fontWeight: 700,
                    letterSpacing: 2, color: done ? UI.good : UI.text,
                  }}>{t.name.toUpperCase()}</span>
                  <span style={{
                    fontSize: 8, letterSpacing: 2, padding: "2px 6px",
                    background: `${m.tint}22`, color: m.tint,
                    clipPath: facetTB(3),
                  }}>{m.label}</span>
                  {done && (
                    <span style={{
                      fontSize: 10, color: UI.good, textShadow: glow(UI.good),
                    }}>✓</span>
                  )}
                </div>
                <div style={{
                  fontSize: 11, color: UI.textDim, fontStyle: "italic",
                  letterSpacing: 0.5,
                }}>{t.blurb}</div>
                <div style={{
                  fontFamily: UI.fontNum, fontSize: 9, color: UI.textFaint,
                  marginTop: 5, display: "flex", gap: 12,
                }}>
                  <span>{"★".repeat(t.difficulty)}{"☆".repeat(4 - t.difficulty)}</span>
                  <span>{t.balls} balls</span>
                  {t.loadout && <span style={{ color: UI.brassDim }}>FIXED KIT</span>}
                </div>
              </div>

              {/* The persisted number. A score table with no best is
                  an invitation; one with a best is a challenge. */}
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{
                  fontSize: 8, letterSpacing: 2, color: UI.textFaint,
                }}>BEST</div>
                <div style={{
                  fontFamily: UI.fontNum, fontSize: 15, letterSpacing: 1,
                  color: hi ? UI.brassBright : UI.panelEdge,
                  textShadow: hi ? glow(UI.brassBright) : "none",
                }}>
                  {hi ? commas(hi) : "—"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Shell>
  );
}

// A tiny abstract rendering of the table's shape. Enough to be
// recognizable, cheap enough to not need art. (Structure first;
// the art overlay lands later and 404s gracefully until it does.)
function TableThumb({ table }) {
  const m = KIND_META[table.kind];
  return (
    <div style={{
      width: 44, height: 68, flexShrink: 0,
      background: `linear-gradient(175deg, ${PF.deck2}, ${PF.deck0})`,
      clipPath: facetTB(5),
      boxShadow: `inset 0 0 0 1px ${m.tint}33`,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{
        position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)",
        width: 26, height: 14, borderRadius: "50%",
        border: `1px solid ${m.tint}55`,
      }} />
      <div style={{
        position: "absolute", bottom: 10, left: 8, width: 12, height: 2,
        background: PF.flipper, transform: "rotate(24deg)",
      }} />
      <div style={{
        position: "absolute", bottom: 10, right: 8, width: 12, height: 2,
        background: PF.flipper, transform: "rotate(-24deg)",
      }} />
    </div>
  );
}

// ── LOADOUT ───────────────────────────────────────────────────
export function LoadoutScreen({ loadout, onSave, onBack }) {
  ensureKeyframes();
  const [sel, setSel] = useState(validateLoadout(loadout));

  const toggle = (id) => {
    if (id === "nudge") return;                 // always free, never a slot
    setSel((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= LOADOUT_SIZE) return cur;
      return [...cur, id];
    });
  };

  const pickable = POWER_LIST.filter((p) => p.id !== "nudge");

  return (
    <Shell title="LOADOUT" onBack={onBack}>
      <div style={{ width: 560 }}>
        <div style={{
          fontSize: 11, color: UI.textDim, marginBottom: 16,
          letterSpacing: 1, lineHeight: 1.6,
        }}>
          Choose <span style={{ color: UI.brassBright }}>{LOADOUT_SIZE}</span>.
          You bring them to every table. <span style={{ color: UI.hot }}>☠</span> can
          be aimed at an opponent. <span style={{ color: UI.good }}>Nudge is always free.</span>
        </div>

        <div style={{
          display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10,
        }}>
          {pickable.map((p) => {
            const on = sel.includes(p.id);
            const tint = tintOf(p.tint);
            const full = sel.length >= LOADOUT_SIZE && !on;
            return (
              <button
                key={p.id}
                onClick={() => toggle(p.id)}
                disabled={full}
                style={{
                  padding: "12px 8px", clipPath: facet(8),
                  background: on ? PANEL_LIT : PANEL, border: "none",
                  boxShadow: on ? bloom(tint) : RAISED,
                  opacity: full ? 0.32 : 1,
                  cursor: full ? "not-allowed" : "pointer",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", gap: 4,
                  transition: "box-shadow 140ms, opacity 140ms",
                }}
              >
                <span style={{
                  fontSize: 24, color: tint,
                  textShadow: on ? glow(tint) : "none",
                }}>{p.glyph}</span>
                <span style={{
                  fontFamily: UI.fontD, fontSize: 10, letterSpacing: 1,
                  color: on ? UI.text : UI.textDim,
                }}>{p.name.toUpperCase()}</span>
                <span style={{ fontFamily: UI.fontNum, fontSize: 10, color: UI.brassDim }}>
                  {p.cost}
                </span>
                {p.sabotage && (
                  <span style={{ fontSize: 9, color: UI.hot }}>☠</span>
                )}
                <span style={{
                  fontSize: 8, color: UI.textFaint, textAlign: "center",
                  lineHeight: 1.4, minHeight: 22, marginTop: 2,
                }}>{p.blurb}</span>
              </button>
            );
          })}
        </div>

        <div style={{
          marginTop: 20, display: "flex", alignItems: "center", gap: 14,
        }}>
          <div style={{ display: "flex", gap: 6, flex: 1 }}>
            {Array.from({ length: LOADOUT_SIZE }).map((_, i) => {
              const id = sel[i];
              const p = id ? getPower(id) : null;
              return (
                <div key={i} style={{
                  width: 44, height: 44, clipPath: facet(6),
                  background: p ? PANEL_LIT : "#0a0c15",
                  boxShadow: p ? RAISED : "inset 0 2px 8px rgba(0,0,0,0.8)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 19,
                  color: p ? tintOf(p.tint) : UI.textFaint,
                  textShadow: p ? glow(tintOf(p.tint)) : "none",
                }}>
                  {p ? p.glyph : "·"}
                </div>
              );
            })}
          </div>
          <button
            onClick={() => onSave(validateLoadout(sel))}
            style={btnStyle(UI.brassBright)}
          >
            SAVE
          </button>
        </div>
      </div>
    </Shell>
  );
}

// ── GAUNTLET ──────────────────────────────────────────────────
export function GauntletScreen({ onPick, onBack, cleared = {} }) {
  ensureKeyframes();
  return (
    <Shell title="THE GAUNTLET" onBack={onBack}>
      <div style={{
        fontSize: 11, color: UI.textDim, marginBottom: 16,
        letterSpacing: 1, width: 480, lineHeight: 1.6,
      }}>
        Seven opponents. Each plays a real table, for real, and can lose.
        Skill climbs. <span style={{ color: UI.hot }}>Temperament rotates</span> —
        beating a Rabid teaches you nothing about beating a Patient.
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 9, width: 480 }}>
        {GAUNTLET.map((n, i) => {
          const done = has(cleared, n.id);
          const locked = i > 0 && !has(cleared, GAUNTLET[i - 1].id) && i > count(cleared);
          return (
            <button
              key={n.id}
              onClick={() => !locked && onPick(n)}
              disabled={locked}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "12px 16px", clipPath: facetTB(8),
                background: done ? PANEL_LIT : PANEL, border: "none",
                boxShadow: done ? bloom(UI.good) : RAISED,
                opacity: locked ? 0.3 : 1,
                cursor: locked ? "not-allowed" : "pointer",
                textAlign: "left", width: "100%",
              }}
            >
              <div style={{
                width: 34, height: 34, clipPath: facet(5),
                background: `linear-gradient(174deg, ${UI.hotDim}, ${UI.panel})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 15, color: UI.hot, textShadow: glow(UI.hot),
                flexShrink: 0,
              }}>
                {locked ? "🔒" : done ? "✓" : i + 1}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontFamily: UI.fontD, fontSize: 13, letterSpacing: 2,
                  color: done ? UI.good : UI.text,
                }}>
                  {n.name.toUpperCase()}
                </div>
                <div style={{ fontSize: 10, color: UI.textFaint, fontStyle: "italic" }}>
                  {n.blurb}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{
                  fontFamily: UI.fontNum, fontSize: 9, color: UI.textFaint,
                  letterSpacing: 1,
                }}>
                  {n.temperament.toUpperCase()}
                </div>
                <div style={{ marginTop: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
                  {Array.from({ length: 5 }).map((_, k) => (
                    <div key={k} style={{
                      width: 5, height: 9,
                      background: k < Math.round(n.skill * 5) ? UI.hot : UI.panelEdge,
                    }} />
                  ))}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Shell>
  );
}

// ── Shared shell ──────────────────────────────────────────────
function Shell({ title, onBack, children }) {
  return (
    <div style={{
      minHeight: "100vh", background: PAGE_BG, fontFamily: UI.font,
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "40px 20px",
    }}>
      <div style={{
        fontFamily: UI.fontD, fontSize: 22, fontWeight: 900, letterSpacing: 8,
        color: UI.brassBright, textShadow: glow(UI.brassBright),
        marginBottom: 26,
      }}>
        {title}
      </div>
      {children}
      <button onClick={onBack} style={{
        ...btnStyle(UI.chromeDim), marginTop: 28, padding: "8px 20px",
      }}>
        ← BACK
      </button>
    </div>
  );
}
